import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCriticalThinkingItems } from '../lib/api';

type Cat = { key: string; label: string };

const DISCIPLINE_META: Record<string, { title: string; subtitle: string; categories: Cat[] }> = {
  nursing: {
    title: 'Nursing',
    subtitle: 'Case studies, pharmacology rationales, and clinical stories',
    categories: [
      { key: '', label: 'All' },
      { key: 'case_study', label: 'Case Studies' },
      { key: 'pharmacology', label: 'Pharmacology' },
      { key: 'med_surg', label: 'Med Surg' },
      { key: 'icu', label: 'ICU' },
      { key: 'er', label: 'ER' },
    ],
  },
  medicine: {
    title: 'Medicine',
    subtitle: 'Clinical cases, diagnostic reasoning, pharmacology, and emergencies',
    categories: [
      { key: '', label: 'All' },
      { key: 'med_case', label: 'Clinical Cases' },
      { key: 'med_diagnosis', label: 'Diagnosis' },
      { key: 'med_pharm', label: 'Pharmacology' },
      { key: 'med_emergency', label: 'Emergencies' },
      { key: 'med_ethics', label: 'Ethics & EBM' },
    ],
  },
  law: {
    title: 'Law',
    subtitle: 'Case briefs, doctrine, issue-spotting, and exam prep',
    categories: [
      { key: '', label: 'All' },
      { key: 'law_case', label: 'Case Briefs' },
      { key: 'law_doctrine', label: 'Doctrine' },
      { key: 'law_issue', label: 'Issue Spotting' },
      { key: 'law_exam', label: 'Exam Prep' },
      { key: 'law_ethics', label: 'Ethics' },
    ],
  },
};

const CATEGORY_ICONS: Record<string, string> = {
  case_study: '📋', pharmacology: '💊', med_surg: '🏥', icu: '🫀', er: '🚑',
  med_case: '🩺', med_diagnosis: '🔬', med_pharm: '💊', med_emergency: '🚨', med_ethics: '⚖️',
  law_case: '📖', law_doctrine: '📜', law_issue: '🔎', law_exam: '📝', law_ethics: '⚖️',
};

const CATEGORY_LABELS: Record<string, string> = {
  case_study: 'Case Study', pharmacology: 'Pharmacology', med_surg: 'Med Surg', icu: 'ICU', er: 'ER',
  med_case: 'Clinical Case', med_diagnosis: 'Diagnosis', med_pharm: 'Pharmacology', med_emergency: 'Emergency', med_ethics: 'Ethics / EBM',
  law_case: 'Case Brief', law_doctrine: 'Doctrine', law_issue: 'Issue Spotting', law_exam: 'Exam', law_ethics: 'Ethics',
};

const CATEGORY_COLORS: Record<string, string> = {
  case_study: 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 border-blue-200',
  pharmacology: 'bg-purple-100 text-purple-700 border-purple-200',
  med_surg: 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 border-emerald-200',
  icu: 'bg-red-100 text-red-700 border-red-200',
  er: 'bg-orange-100 dark:bg-orange-500/20 text-orange-700 dark:text-orange-300 border-orange-200',
  med_case: 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 border-blue-200',
  med_diagnosis: 'bg-cyan-100 dark:bg-cyan-500/20 text-cyan-700 dark:text-cyan-300 border-cyan-200',
  med_pharm: 'bg-purple-100 text-purple-700 border-purple-200',
  med_emergency: 'bg-red-100 text-red-700 border-red-200',
  med_ethics: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  law_case: 'bg-amber-100 dark:bg-amber-500/20 text-amber-700 border-amber-200',
  law_doctrine: 'bg-orange-100 dark:bg-orange-500/20 text-orange-700 dark:text-orange-300 border-orange-200',
  law_issue: 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 border-emerald-200',
  law_exam: 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 border-blue-200',
  law_ethics: 'bg-indigo-100 text-indigo-700 border-indigo-200',
};

function difficultyLabel(d: number) {
  if (d <= 2) return 'Beginner';
  if (d <= 3) return 'Intermediate';
  if (d <= 4) return 'Advanced';
  return 'Expert';
}
function difficultyColor(d: number) {
  if (d <= 2) return 'text-emerald-600';
  if (d <= 3) return 'text-amber-600';
  if (d <= 4) return 'text-orange-600';
  return 'text-red-600';
}

export function CriticalThinkingPage() {
  const navigate = useNavigate();
  const { discipline = 'nursing' } = useParams();
  const meta = (DISCIPLINE_META[discipline] ?? DISCIPLINE_META.nursing)!;
  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');
  const { data, isLoading } = useCriticalThinkingItems(discipline, category || undefined);

  useEffect(() => { window.scrollTo(0, 0); setCategory(''); setSearch(''); }, [discipline]);

  const items = data?.items ?? [];

  // Live filter: kicks in once the user types 2+ characters. Matches the visible
  // text of each item (title, topic, clinical setting, category label).
  const q = search.trim().toLowerCase();
  const searchActive = q.length >= 2;
  const visibleItems = useMemo(() => {
    if (!searchActive) return items;
    return items.filter((it: any) =>
      [it.title, it.topic, it.clinicalSetting, CATEGORY_LABELS[it.category] ?? it.category]
        .some((f) => typeof f === 'string' && f.toLowerCase().includes(q)),
    );
  }, [items, q, searchActive]);

  const grouped = useMemo(() => {
    if (category) return null;
    const groups: Record<string, typeof items> = {};
    for (const item of visibleItems) (groups[item.category] ??= []).push(item);
    return groups;
  }, [visibleItems, category]);

  if (isLoading) {
    return <div className="flex items-center justify-center min-h-[60vh] text-slate-500">Loading...</div>;
  }

  return (
    <div className="max-w-2xl mx-auto pb-20 space-y-4">
      <button onClick={() => navigate('/learn')} className="flex items-center gap-1 text-sm text-slate-500 hover:text-squishy-primary transition">
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" /></svg>
        Learn
      </button>
      <div>
        <h1 className="text-xl font-bold text-squishy-dark">{meta.title} Critical Thinking</h1>
        <p className="text-xs text-slate-500 mt-0.5">{meta.subtitle}</p>
      </div>

      <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-hide">
        {meta.categories.map((c) => (
          <button
            key={c.key}
            onClick={() => setCategory(c.key)}
            className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
              category === c.key ? 'bg-squishy-dark text-white shadow-sm' : 'bg-white text-slate-600 border border-slate-200 hover:border-slate-300'
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative">
        <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35m1.35-5.4a6.75 6.75 0 11-13.5 0 6.75 6.75 0 0113.5 0z" />
        </svg>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={`Search ${meta.title} cases...`}
          className="w-full bg-white border border-slate-200 rounded-full pl-9 pr-9 py-2 text-sm text-squishy-dark placeholder-slate-400 focus:outline-none focus:border-squishy-primary"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch('')}
            aria-label="Clear search"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-lg leading-none"
          >×</button>
        )}
      </div>

      {searchActive && visibleItems.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-8">No results for "{search.trim()}".</p>
      ) : category ? (
        <div className="space-y-2">
          {visibleItems.map((item) => (
            <ItemCard key={item.id} item={item} onClick={() => navigate(`/learn/${discipline}/${item.externalId}`)} />
          ))}
          {visibleItems.length === 0 && <p className="text-sm text-slate-400 text-center py-8">No items in this category yet.</p>}
        </div>
      ) : (
        Object.entries(grouped ?? {}).map(([cat, catItems]) => (
          <div key={cat}>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">{CATEGORY_ICONS[cat] ?? '📖'}</span>
              <h2 className="text-sm font-bold text-squishy-dark">{CATEGORY_LABELS[cat] ?? cat.replace(/_/g, ' ')}</h2>
              <span className="text-[10px] text-slate-400 font-medium">{catItems.length} items</span>
            </div>
            <div className="space-y-2">
              {catItems.map((item) => (
                <ItemCard key={item.id} item={item} onClick={() => navigate(`/learn/${discipline}/${item.externalId}`)} />
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

function ItemCard({ item, onClick }: { item: any; onClick: () => void }) {
  const colorClass = CATEGORY_COLORS[item.category] ?? 'bg-slate-100 text-slate-600 border-slate-200';
  return (
    <button
      onClick={onClick}
      className="w-full text-left bg-white border border-slate-200 rounded-xl px-4 py-3 hover:border-squishy-primary hover:shadow-sm transition-all active:scale-[0.99]"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-squishy-dark leading-snug">{item.title}</h3>
          <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{item.topic}</p>
        </div>
        <span className={`shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full border ${colorClass}`}>
          {(CATEGORY_LABELS[item.category] ?? item.category.replace(/_/g, ' ')).toUpperCase()}
        </span>
      </div>
      <div className="flex items-center gap-3 mt-2">
        <span className={`text-[10px] font-semibold ${difficultyColor(item.difficulty)}`}>{difficultyLabel(item.difficulty)}</span>
        {item.clinicalSetting && <span className="text-[10px] text-slate-400 truncate max-w-[150px]">{item.clinicalSetting}</span>}
        <div className="flex gap-0.5 ml-auto">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className={`w-1.5 h-1.5 rounded-full ${i < item.difficulty ? 'bg-squishy-primary' : 'bg-slate-200'}`} />
          ))}
        </div>
      </div>
    </button>
  );
}
